import { NgModule, inject } from '@angular/core';
import { RouterModule, type Routes } from '@angular/router';

import { AuthGuard } from './authentication/_guards/auth.guard';
import { WebConsoleAccessGuard } from './authentication/_guards/web-console-access.guard';
import { AuthService } from './authentication/_services/auth.service';
import { AuthLayoutComponent } from './shared/_layouts/auth-layout/auth-layout.component';
import { FullComponent } from './shared/_layouts/full/full.component';
import { ComingSoonComponent } from './shared/coming-soon/coming-soon';
import { AccessDeniedComponent } from './shared/components/access-denied/access-denied.component';
import { WebConsoleRestrictedComponent } from './shared/components/web-console-restricted/web-console-restricted.component';

const routes: Routes = [
  // Redirect root to login
  { path: '', redirectTo: '/login', pathMatch: 'full' },

  // Authentication routes
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./authentication/authentication.module').then(m => m.AuthenticationModule)
      }
    ]
  },
  {
    path: '',
    component: FullComponent,
    // Unguarded on purpose: this is exactly where WebConsoleAccessGuard below sends non-ClientAdmin
    // members after login, so it can't itself be gated by that same guard (infinite redirect).
    children: [
      { path: 'access-denied', component: AccessDeniedComponent },
      { path: 'web-console-restricted', component: WebConsoleRestrictedComponent }
    ]
  },

  // Main application routes
  {
    path: '',
    // WebConsoleAccessGuard admits officers (ClientAdmin) and the ApplicationSupport login, and nobody
    // else (see its own doc comment). ApplicationSupport has no Member record, so every chapter screen
    // below is `roles: ['ClientAdmin']`; `roles: []` is only for screens any login can use.
    canActivate: [AuthGuard, WebConsoleAccessGuard],
    component: FullComponent,
    children: [
      {
        path: 'logs', // Do not move this path, it must be the first child of FullComponent
        loadChildren: () => import('./modules/logs/log-file.module').then(m => m.LogModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ApplicationSupport']
        }
      },
      {
        path: 'master-data/translations',
        loadChildren: () => import('./modules/master-data/translations/translation.module').then(m => m.TranslationModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ApplicationSupport']
        }
      },
      {
        path: 'change-password',
        loadChildren: () => import('./modules/change-password/change-password.module').then(m => m.ChangePasswordModule),
        canMatch: [AuthGuard],
        data: {
          roles: [] // Allow all authenticated users to access change-password
        }
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin']
        }
      },
      {
        path: 'members',
        loadChildren: () => import('./modules/member/member.module').then(m => m.MemberModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // officers only (not ApplicationSupport); create/edit/bulk-import also gated per-route inside MemberRoutingModule
        }
      },
      {
        path: 'events',
        loadChildren: () => import('./modules/event/event.module').then(m => m.EventModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // officers only (not ApplicationSupport); create/edit/attendance also gated per-route inside EventRoutingModule
        }
      },
      {
        path: 'referrals',
        loadChildren: () => import('./modules/referral/referral.module').then(m => m.ReferralModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // TDD 01 §8 - every screen in this module is officer-only (chapter-wide admin view + testimonial moderation); no member-facing route
        }
      },
      {
        path: 'announcements',
        loadChildren: () => import('./modules/announcement/announcement.module').then(m => m.AnnouncementModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // TDD 08 §8 - both tabs (custom compose/send + system template/threshold config) are officer-only
        }
      },
      {
        path: 'rr-meetings',
        loadChildren: () => import('./modules/rrmeeting/rrmeeting.module').then(m => m.RrMeetingModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // TDD 11 §8/§11 - read-only chapter-wide oversight only; create/respond/complete/cancel/edit are two-party member actions in Flutter
        }
      },
      {
        path: 'achievements',
        loadChildren: () => import('./modules/achievement/achievement.module').then(m => m.AchievementModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // TDD 12 §2/§7.1/§8 - admin badge-threshold configuration only; badge/leaderboard display itself is member-facing (Flutter)
        }
      },
      {
        path: 'reporting',
        loadChildren: () => import('./modules/reporting/reporting.module').then(m => m.ReportingModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // TDD 07 §7.1/§8 - admin-only, no member-facing version of this screen
        }
      },
      {
        path: 'handover',
        loadChildren: () => import('./modules/handover/handover.module').then(m => m.HandoverModule),
        canMatch: [AuthGuard],
        data: {
          // TDD 09 §7.1/§8, revised August 2026 (TDD 06's role-model revision) - Chairman is no longer its
          // own Angular-checkable role (see Member.leadershipTitle's javadoc), so this route-level gate is
          // just "any ClientAdmin may reach the screen"; OfficerChangesComponent itself further restricts
          // recording/cancelling officer changes to Chairman/Charter Chairman (any officer when the chapter
          // has neither), matching the backend's authoritative MemberService#requireChairmanAuthority.
          roles: ['ClientAdmin']
        }
      },
      {
        path: 'chapter-settings',
        loadChildren: () => import('./modules/chapter/chapter.module').then(m => m.ChapterModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['ClientAdmin'] // TDD 02 §7.2/TDD 10 §7 - matches ChapterController's own hasAnyRole gate, all three officers full admins here
        }
      },
      {
        path: 'coming-soon',
        component: ComingSoonComponent,
        canMatch: [AuthGuard],
        data: { roles: [] }
      },
      {
        path: 'master-data/states',
        loadChildren: () => import('./modules/master-data/states/state.module').then(m => m.StateModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/countries',
        loadChildren: () => import('./modules/master-data/countries/country.module').then(m => m.CountryModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/currencies',
        loadChildren: () => import('./modules/master-data/currencies/currency.module').then(m => m.CurrencyModule),
        canMatch: [AuthGuard],
        data: {
          // roles: ['ROLE_ClientAdmin']
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'notifications',
        loadChildren: () => import('./modules/notifications/notification.module').then(m => m.NotificationModule),
        canMatch: [AuthGuard],
        data: {
          roles: [] // Allow all authenticated users to access notifications
        }
      },
      {
        path: 'master-data/units',
        loadChildren: () => import('./modules/master-data/units/unit.module').then(m => m.UnitModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/reference-types',
        loadChildren: () => import('./modules/master-data/reference-types/reference-type.module').then(m => m.ReferenceTypeModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },
      {
        path: 'master-data/reference-values',
        loadChildren: () => import('./modules/master-data/reference-values/reference-value.module').then(m => m.ReferenceValueModule),
        canMatch: [AuthGuard],
        data: {
          roles: ['SimpleMasterDataRead']
        }
      },

      // Unknown routes within the FullComponent layout go to the user's home: the dashboard, or Logs for ApplicationSupport
      { path: '**', redirectTo: () => inject(AuthService).homeRoute, pathMatch: 'full' }
    ]
  },

  // Wildcard route for undefined paths
  // This should be placed at the end
  { path: '**', redirectTo: '/login', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
